import { Component, OnInit, OnDestroy } from '@angular/core';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { ReservationService } from 'src/app/services/reservation.service';
import { NotificationToastService } from 'src/app/services/notification-toast.service';
import { ActivatedRoute } from '@angular/router';
import { ReservationSearchItem } from 'src/app/model/reservation-search-item';

@Component({
  selector: 'app-patient-crm-medical-appointment',
  templateUrl: './patient-crm-medical-appointment.component.html',
  styleUrls: ['./patient-crm-medical-appointment.component.css']
})
export class PatientCrmMedicalAppointmentComponent implements OnInit, OnDestroy {

  loading: boolean;

  columnsDefinition: { field: string; title: string }[] = [
    { field: 'DateTime', title: 'Horario inicio' },
    { field: 'EndHour', title: 'Horario fin' },
    { field: 'StateName', title: 'Estado' },
    { field: 'PrincipalPracticeName', title: 'Práctica' },
  ];

  madeColumnsDefinition: { field: string; title: string }[] = [
    { field: 'DateTime', title: 'Horario inicio' },
    { field: 'EndHour', title: 'Horario fin' },
    { field: 'Protocol', title: 'Protocolo' },
    { field: 'StateName', title: 'Estado' },
    { field: 'PrincipalPracticeName', title: 'Práctica' },
  ];

  _columnOrder: string[] = this.columnsDefinition.map(c => c.field);
  _madeColumnOrder: string[] = this.madeColumnsDefinition.map(c => c.field);
  columnWidths: { [key: string]: number } = {};
  pendingColumnFilters: { [key: string]: string } = {};
  madeColumnFilters: { [key: string]: string } = {};
  inconsistentColumnFilters: { [key: string]: string } = {};

  _pendingSortColumn: string = null;
  _pendingSortDirection: 'asc' | 'desc' = 'asc';
  _madeSortColumn: string = null;
  _madeSortDirection: 'asc' | 'desc' = 'asc';
  _inconsistentSortColumn: string = null;
  _inconsistentSortDirection: 'asc' | 'desc' = 'asc';

  _pendingAllItems: ReservationSearchItem[] = [];
  _madeAllItems: ReservationSearchItem[] = [];
  _inconsistentAllItems: ReservationSearchItem[] = [];
  pendingDataSource: ReservationSearchItem[] = [];
  madeDataSource: ReservationSearchItem[] = [];
  inconsistentDataSource: ReservationSearchItem[] = [];

  private _resizingField: string = null;
  private _resizeStartX: number = 0;
  private _resizeStartWidth: number = 0;
  private _dragSourceField: string = null;
  private _resizeMouseMoveBound: (e: MouseEvent) => void;
  private _resizeMouseUpBound: (e: MouseEvent) => void;

  constructor(
    private notificationToastService: NotificationToastService,
    private reservationService: ReservationService,
    private route: ActivatedRoute
  ) {
    this._resizeMouseMoveBound = (e: MouseEvent) => this.onResizeMouseMove(e);
    this._resizeMouseUpBound = (e: MouseEvent) => this.onResizeMouseUp(e);
  }

  get orderedColumnsDefinition(): { field: string; title: string }[] {
    return this._columnOrder
      .filter(f => this.columnsDefinition.some(c => c.field === f))
      .map(f => this.columnsDefinition.find(c => c.field === f));
  }

  get orderedMadeColumnsDefinition(): { field: string; title: string }[] {
    return this._madeColumnOrder
      .filter(f => this.madeColumnsDefinition.some(c => c.field === f))
      .map(f => this.madeColumnsDefinition.find(c => c.field === f));
  }

  getColumnWidth(field: string): number {
    return this.columnWidths[field] != null ? this.columnWidths[field] : 150;
  }

  private loadColumnState() {
    try {
      const savedOrder = sessionStorage.getItem('patient-crm-medical-appointment.columns.v1');
      if (savedOrder) {
        const saved: string[] = JSON.parse(savedOrder);
        const valid = saved.filter(f => this.columnsDefinition.some(c => c.field === f));
        const missing = this.columnsDefinition.map(c => c.field).filter(f => !valid.includes(f));
        this._columnOrder = valid.concat(missing);
      }
      const savedMadeOrder = sessionStorage.getItem('patient-crm-medical-appointment.made-columns.v1');
      if (savedMadeOrder) {
        const saved: string[] = JSON.parse(savedMadeOrder);
        const valid = saved.filter(f => this.madeColumnsDefinition.some(c => c.field === f));
        const missing = this.madeColumnsDefinition.map(c => c.field).filter(f => !valid.includes(f));
        this._madeColumnOrder = valid.concat(missing);
      }
      const savedWidths = sessionStorage.getItem('patient-crm-medical-appointment.column-widths.v1');
      if (savedWidths) {
        this.columnWidths = JSON.parse(savedWidths);
      }
    } catch (e) { /* ignore */ }
  }

  private saveColumnState() {
    try {
      sessionStorage.setItem('patient-crm-medical-appointment.columns.v1', JSON.stringify(this._columnOrder));
      sessionStorage.setItem('patient-crm-medical-appointment.made-columns.v1', JSON.stringify(this._madeColumnOrder));
      sessionStorage.setItem('patient-crm-medical-appointment.column-widths.v1', JSON.stringify(this.columnWidths));
    } catch (e) { /* ignore */ }
  }

  onHeaderDragStart(event: DragEvent, field: string) {
    this._dragSourceField = field;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  onHeaderDragOver(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  onHeaderDrop(event: DragEvent, targetField: string) {
    event.preventDefault();
    if (this._dragSourceField && this._dragSourceField !== targetField) {
      const fromIndex = this._columnOrder.indexOf(this._dragSourceField);
      const toIndex = this._columnOrder.indexOf(targetField);
      if (fromIndex !== -1 && toIndex !== -1) {
        moveItemInArray(this._columnOrder, fromIndex, toIndex);
        this.saveColumnState();
      }
    }
  }

  onMadeHeaderDrop(event: DragEvent, targetField: string) {
    event.preventDefault();
    if (this._dragSourceField && this._dragSourceField !== targetField) {
      const fromIndex = this._madeColumnOrder.indexOf(this._dragSourceField);
      const toIndex = this._madeColumnOrder.indexOf(targetField);
      if (fromIndex !== -1 && toIndex !== -1) {
        moveItemInArray(this._madeColumnOrder, fromIndex, toIndex);
        this.saveColumnState();
      }
    }
  }

  onHeaderDragEnd() {
    this._dragSourceField = null;
  }

  startResize(event: MouseEvent, field: string) {
    event.preventDefault();
    event.stopPropagation();
    this._resizingField = field;
    this._resizeStartX = event.clientX;
    this._resizeStartWidth = this.getColumnWidth(field);
    document.addEventListener('mousemove', this._resizeMouseMoveBound);
    document.addEventListener('mouseup', this._resizeMouseUpBound);
  }

  private onResizeMouseMove(event: MouseEvent) {
    if (this._resizingField) {
      const diff = event.clientX - this._resizeStartX;
      const newWidth = Math.max(60, this._resizeStartWidth + diff);
      this.columnWidths[this._resizingField] = newWidth;
    }
  }

  private onResizeMouseUp(event: MouseEvent) {
    if (this._resizingField) {
      document.removeEventListener('mousemove', this._resizeMouseMoveBound);
      document.removeEventListener('mouseup', this._resizeMouseUpBound);
      this._resizingField = null;
      this.saveColumnState();
    }
  }

  ngOnDestroy() {
    document.removeEventListener('mousemove', this._resizeMouseMoveBound);
    document.removeEventListener('mouseup', this._resizeMouseUpBound);
  }

  sortByColumn(grid: 'pending' | 'made' | 'inconsistent', field: string) {
    if (grid === 'pending') {
      if (this._pendingSortColumn === field) {
        this._pendingSortDirection = this._pendingSortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this._pendingSortColumn = field;
        this._pendingSortDirection = 'asc';
      }
    } else if (grid === 'made') {
      if (this._madeSortColumn === field) {
        this._madeSortDirection = this._madeSortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this._madeSortColumn = field;
        this._madeSortDirection = 'asc';
      }
    } else {
      if (this._inconsistentSortColumn === field) {
        this._inconsistentSortDirection = this._inconsistentSortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this._inconsistentSortColumn = field;
        this._inconsistentSortDirection = 'asc';
      }
    }
    this.applyColumnFilters(grid);
  }

  getSortIcon(grid: 'pending' | 'made' | 'inconsistent', field: string): string {
    let col: string;
    let dir: 'asc' | 'desc';
    if (grid === 'pending') {
      col = this._pendingSortColumn; dir = this._pendingSortDirection;
    } else if (grid === 'made') {
      col = this._madeSortColumn; dir = this._madeSortDirection;
    } else {
      col = this._inconsistentSortColumn; dir = this._inconsistentSortDirection;
    }
    if (col !== field) { return 'unfold_more'; }
    return dir === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  onColumnFilterInput(grid: 'pending' | 'made' | 'inconsistent', field: string, value: string) {
    if (grid === 'pending') { this.pendingColumnFilters[field] = value; }
    else if (grid === 'made') { this.madeColumnFilters[field] = value; }
    else { this.inconsistentColumnFilters[field] = value; }
    this.applyColumnFilters(grid);
  }

  applyColumnFilters(grid: 'pending' | 'made' | 'inconsistent') {
    let allItems: ReservationSearchItem[];
    let filters: { [key: string]: string };
    let sortColumn: string;
    let sortDirection: 'asc' | 'desc';
    if (grid === 'pending') {
      allItems = this._pendingAllItems; filters = this.pendingColumnFilters;
      sortColumn = this._pendingSortColumn; sortDirection = this._pendingSortDirection;
    } else if (grid === 'made') {
      allItems = this._madeAllItems; filters = this.madeColumnFilters;
      sortColumn = this._madeSortColumn; sortDirection = this._madeSortDirection;
    } else {
      allItems = this._inconsistentAllItems; filters = this.inconsistentColumnFilters;
      sortColumn = this._inconsistentSortColumn; sortDirection = this._inconsistentSortDirection;
    }
    let filtered = allItems != null ? allItems.slice() : [];
    Object.keys(filters).forEach(field => {
      const filterVal = filters[field];
      if (filterVal && filterVal.trim() !== '') {
        const lower = filterVal.toLowerCase();
        filtered = filtered.filter(item => {
          const cell = this.formatCellValue(item, field);
          return cell.toLowerCase().includes(lower);
        });
      }
    });
    if (sortColumn) {
      filtered.sort((a, b) => {
        const av = this.formatCellValue(a, sortColumn);
        const bv = this.formatCellValue(b, sortColumn);
        const cmp = av.localeCompare(bv);
        return sortDirection === 'asc' ? cmp : -cmp;
      });
    }
    if (grid === 'pending') { this.pendingDataSource = filtered; }
    else if (grid === 'made') { this.madeDataSource = filtered; }
    else { this.inconsistentDataSource = filtered; }
  }

  formatCellValue(element: ReservationSearchItem, field: string): string {
    if (element == null) { return ''; }
    const val = element[field];
    return val != null ? String(val) : '';
  }

  ngOnInit() {
    this.loadColumnState();
    this.loading = true;
    this.route.params.subscribe((params) => {
      this.reservationService.getReservationSearchItems(true, true, true, null, null, null, params['Id'])
        .then((info) => {
          if (info) {
            this._pendingAllItems = info.filter(i => i.IsPendient === true);
            this._madeAllItems = info.filter(i => i.IsPendient === false);
            this.applyColumnFilters('pending');
            this.applyColumnFilters('made');
          }
          this.loading = false;
        })
        .catch((err) => this.notificationToastService.error(JSON.stringify(err.error), 'Turnos'));

      this.reservationService.getReservationSearchItemsForInconsistent(null, null, params['Id'], null, null, null, null, null)
        .then((info) => {
          if (info) {
            this._inconsistentAllItems = info;
            this.applyColumnFilters('inconsistent');
          }
          this.loading = false;
        })
        .catch((err) => this.notificationToastService.error(JSON.stringify(err.error), 'Turnos inconsistentes'));
    });
  }
}
